import {
  cloneMetadata,
  ensureSorted,
  normalizeMetadataSignature,
  sanitizeTextList,
} from 'utils/responseNormalization';
import type { ResponsePayload } from '../../types';

export interface StoredResponse extends ResponsePayload {
  id: number;
  created_at?: string;
  updated_at?: string;
}

export interface FlowResponseDraft extends ResponsePayload {
  responseId?: number;
}

type NormalizedResponseShape = {
  rating: number | null;
  text: string;
  bool: boolean | null;
  selected: number[];
  revealed: number[];
  custom: string | null;
  audio: string | null;
  textList: string[];
  flowItem: number | null;
  flowRun: number | null;
  completed: boolean | null;
  inputMode: string | null;
  voiceSession: number | null;
  voiceTurn: number | null;
  generationId: number | null;
  metadata: string;
};

export const normalizeResponsePayload = (
  response?: ResponsePayload,
  questionType?: string,
): NormalizedResponseShape => ({
  rating: typeof response?.rating_value === 'number' ? response.rating_value : null,
  text: response?.text_value ?? '',
  bool: typeof response?.boolean_value === 'boolean' ? response.boolean_value : null,
  selected:
    questionType === 'drag_and_drop_ranking'
      ? (Array.isArray(response?.selected_options) ? [...(response?.selected_options ?? [])] : [])
      : ensureSorted(response?.selected_options),
  revealed: ensureSorted(response?.revealed_options),
  custom: response?.custom_other_value ?? null,
  audio: response?.audio_url ?? null,
  textList: sanitizeTextList(response?.text_list),
  flowItem: typeof response?.flow_item === 'number' ? response.flow_item : null,
  flowRun: typeof response?.flow_run === 'number' ? response.flow_run : null,
  completed: typeof response?.is_completed === 'boolean' ? response.is_completed : null,
  inputMode: typeof response?.input_mode === 'string' ? response.input_mode : null,
  voiceSession: typeof response?.voice_session === 'number' ? response.voice_session : null,
  voiceTurn: typeof response?.voice_turn === 'number' ? response.voice_turn : null,
  generationId: typeof response?.generation_id === 'number' ? response.generation_id : null,
  metadata: normalizeMetadataSignature(response?.metadata === undefined ? {} : response.metadata),
});

export const areResponsesEqual = (
  a?: ResponsePayload,
  b?: ResponsePayload,
  questionType?: string,
): boolean => {
  if (!a && !b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  const normalizedA = normalizeResponsePayload(a, questionType);
  const normalizedB = normalizeResponsePayload(b, questionType);

  return (
    normalizedA.rating === normalizedB.rating &&
    normalizedA.text === normalizedB.text &&
    normalizedA.bool === normalizedB.bool &&
    normalizedA.custom === normalizedB.custom &&
    normalizedA.audio === normalizedB.audio &&
    normalizedA.metadata === normalizedB.metadata &&
    normalizedA.completed === normalizedB.completed &&
    normalizedA.inputMode === normalizedB.inputMode &&
    normalizedA.voiceSession === normalizedB.voiceSession &&
    normalizedA.voiceTurn === normalizedB.voiceTurn &&
    normalizedA.generationId === normalizedB.generationId &&
    arraysEqual(normalizedA.textList, normalizedB.textList) &&
    arraysEqual(normalizedA.selected, normalizedB.selected) &&
    arraysEqual(normalizedA.revealed, normalizedB.revealed)
  );
};

const arraysEqual = <T>(left: T[], right: T[]) => {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
};

export const mapApiResponseToStored = (raw: any): StoredResponse | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = typeof raw.id === 'number' ? raw.id : undefined;
  const questionId =
    typeof raw.question === 'number'
      ? raw.question
      : typeof raw.question?.id === 'number'
        ? raw.question.id
        : undefined;

  if (typeof id !== 'number' || typeof questionId !== 'number') {
    return null;
  }

  const selectedDetails = Array.isArray(raw.selected_option_details) ? raw.selected_option_details : [];
  const orderedSelectedDetails = selectedDetails
    .map((detail: any, index: number) => ({
      ...detail,
      __fallbackIndex: index,
    }))
    .sort((left: any, right: any) => {
      const leftHasOrder = typeof left?.response_order === 'number';
      const rightHasOrder = typeof right?.response_order === 'number';

      if (leftHasOrder && rightHasOrder) {
        return left.response_order - right.response_order;
      }

      if (leftHasOrder) {
        return -1;
      }

      if (rightHasOrder) {
        return 1;
      }

      return (left?.__fallbackIndex ?? 0) - (right?.__fallbackIndex ?? 0);
    });
  const selectedOptionsFromDetails = orderedSelectedDetails
    .map((detail: any) => (typeof detail?.id === 'number' ? detail.id : null))
    .filter((value: number | null): value is number => value !== null);
  const selectedOptions = selectedOptionsFromDetails.length
    ? selectedOptionsFromDetails
    : Array.isArray(raw.selected_options)
      ? raw.selected_options
      : [];

  const revealedOptions = Array.isArray(raw.revealed_options)
    ? raw.revealed_options.filter((value: any): value is number => typeof value === 'number')
    : undefined;

  return {
    id,
    question: questionId,
    generation_id: typeof raw.generation_id === 'number' ? raw.generation_id
      : typeof raw.metadata?.ai_generation?.snapshot_id === 'number' ? raw.metadata.ai_generation.snapshot_id : undefined,
    flow_item: typeof raw.flow_item === 'number' ? raw.flow_item : undefined,
    flow_run: typeof raw.flow_run === 'number' ? raw.flow_run : undefined,
    is_completed: typeof raw.is_completed === 'boolean' ? raw.is_completed : false,
    input_mode: raw.input_mode === 'voice' ? 'voice' : raw.input_mode === 'standard' ? 'standard' : undefined,
    voice_session: typeof raw.voice_session === 'number' ? raw.voice_session : undefined,
    voice_turn: typeof raw.voice_turn === 'number' ? raw.voice_turn : undefined,
    rating_value: typeof raw.rating_value === 'number' ? raw.rating_value : undefined,
    text_value: typeof raw.text_value === 'string' ? raw.text_value : undefined,
    boolean_value: typeof raw.boolean_value === 'boolean' ? raw.boolean_value : undefined,
    selected_options: selectedOptions.length ? selectedOptions : undefined,
    revealed_options: revealedOptions && revealedOptions.length ? revealedOptions : undefined,
    custom_other_value: typeof raw.custom_other_value === 'string' ? raw.custom_other_value : undefined,
    audio_url: typeof raw.audio_url === 'string' ? raw.audio_url : undefined,
    text_list: Array.isArray(raw.text_list) ? sanitizeTextList(raw.text_list) : undefined,
    metadata: cloneMetadata(raw.metadata),
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
  };
};

export const storedToDraft = (stored: StoredResponse): FlowResponseDraft => ({
  responseId: stored.id,
  question: stored.question,
  generation_id: stored.generation_id,
  flow_item: stored.flow_item,
  flow_run: stored.flow_run,
  is_completed: stored.is_completed,
  input_mode: stored.input_mode,
  voice_session: stored.voice_session,
  voice_turn: stored.voice_turn,
  rating_value: stored.rating_value,
  text_value: stored.text_value,
  boolean_value: stored.boolean_value,
  selected_options: stored.selected_options ? [...stored.selected_options] : undefined,
  revealed_options: stored.revealed_options ? [...stored.revealed_options] : undefined,
  custom_other_value: stored.custom_other_value,
  audio_url: stored.audio_url,
  text_list: stored.text_list ? [...stored.text_list] : undefined,
  metadata: cloneMetadata(stored.metadata),
});

export const buildRequestPayload = (
  draft: FlowResponseDraft,
  questionId: number,
  flowItemId?: number,
  flowRunId?: number | null,
): ResponsePayload => {
  const payload: ResponsePayload = {
    question: questionId,
    generation_id: draft.generation_id,
    input_mode: 'standard',
    rating_value: draft.rating_value,
    text_value: draft.text_value,
    boolean_value: draft.boolean_value,
    selected_options: draft.selected_options ? [...draft.selected_options] : undefined,
    revealed_options: draft.revealed_options ? [...draft.revealed_options] : undefined,
    custom_other_value: draft.custom_other_value,
    audio_url: draft.audio_url,
  };

  if (typeof draft.is_completed === 'boolean') {
    payload.is_completed = draft.is_completed;
  }

  if (Array.isArray(draft.text_list)) {
    const sanitizedList = sanitizeTextList(draft.text_list);
    if (sanitizedList.length > 0 && sanitizedList.some((item) => item.length > 0)) {
      payload.text_list = sanitizedList;
    }
  }

  const resolvedFlowItem = typeof draft.flow_item === 'number' ? draft.flow_item : flowItemId;
  if (typeof resolvedFlowItem === 'number') {
    payload.flow_item = resolvedFlowItem;
  }

  const resolvedFlowRun = typeof draft.flow_run === 'number' ? draft.flow_run : flowRunId;
  if (typeof resolvedFlowRun === 'number') {
    payload.flow_run = resolvedFlowRun;
  }

  const metadataClone = cloneMetadata(draft.metadata);
  if (typeof metadataClone !== 'undefined') {
    payload.metadata = metadataClone;
  }

  return payload;
};
